// Глобальный слушатель USB HID-сканера (marking_buffer §6).
//
// Активен только на рабочих местах с функцией usb_scan_listener
// (workplace_auth §4, auth_workplaces.features). USB-сканер «печатает»
// код очень быстро и жмёт Enter — серия детектируется HidScanDetector.
// С точки уверенности клавиши перехватываются (preventDefault + blur
// активного поля), подтверждённый скан уходит в тот же пайплайн, что и
// камера: POST /api/marking/scans → общий буфер места → автодобавление
// в открытый ЗН делает существующий useAutoAddBuffer (без изменений).

import { useEffect } from 'react';
import { message } from 'antd';
import { markingApi } from '../api/marking';
import useAuthStore from '../store/authStore';
import useScanBufferStore from '../store/scanBufferStore';
import useBarcodeScanStore from '../store/barcodeScanStore';
import { barcodeScanApi } from '../api/barcodeScans';
import { HidScanDetector } from '../utils/hidScanDetect';
import { isStandardProductBarcode } from '../utils/productBarcode';
import { playScanBeep } from '../utils/scanFeedback';

const handleScan = async (code: string): Promise<void> => {
  playScanBeep();
  try {
    if (isStandardProductBarcode(code)) {
      await barcodeScanApi.create(code);
      await useBarcodeScanStore.getState().refreshItems();
      return;
    }
    const { data: scan } = await markingApi.createScan(code);
    useScanBufferStore.getState().refreshItems();

    if (scan.already_in_user_buffer) {
      if (scan.status === 'reserved' && scan.order_number) {
        message.warning(`Марка уже в буфере. Эта бутылка уже в заказе № ${scan.order_number} — обновлено время сканирования`);
      } else if (scan.status === 'sold') {
        message.warning('Марка уже в буфере. Эта марка уже продана — обновлено время сканирования');
      } else {
        message.warning('Марка уже в буфере — обновлено время сканирования');
      }
      return;
    }
    if (scan.status === 'reserved' && scan.order_number) {
      message.warning(`Эта бутылка уже в заказе № ${scan.order_number}`);
      return;
    }
    if (scan.status === 'sold') {
      message.warning('Эта марка уже продана');
      return;
    }
    if (scan.status === 'off_balance') {
      message.warning('Марка не найдена в справочнике 1С (вне баланса)');
      return;
    }

    if (scan.product_name && scan.size_id) {
      message.success(`${scan.brand || ''} ${scan.product_name} · ${scan.container_size} — сохранено`);
    } else if (scan.gtin) {
      // Модалку привязки не открываем (marking_buffer §6) — только подсказка
      message.info('Новый GTIN — марка в буфере, привяжите на странице «Сканирование»');
    } else {
      message.warning('Не удалось распознать GTIN из кода');
    }
  } catch (e: any) {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 409) {
      message.warning('Эта бутылка уже отсканирована');
    } else {
      message.error(typeof detail === 'string' ? detail : 'Ошибка сохранения скана');
    }
  }
};

const useHidScanListener = (): void => {
  const enabled = useAuthStore(
    (s) => s.user?.workplace_features?.usb_scan_listener === true,
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const detector = new HidScanDetector();

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key.length !== 1) return; // модификаторы/служебные
      const wasConfident = detector.confident;
      const ev = detector.keydown(e.key);
      if (wasConfident || detector.confident) {
        // Это скан — клавиши (включая финальный Enter) не доходят до полей,
        // enterAsTab и antd Select их не видят
        e.preventDefault();
        e.stopPropagation();
        if (ev?.kind === 'burst') {
          // Закрыть выпадайку antd Select и убрать фокус из поля
          (document.activeElement as HTMLElement | null)?.blur?.();
        }
      }
      if (ev?.kind === 'scan') {
        void handleScan(ev.code);
      }
    };

    document.addEventListener('keydown', onKeydown, true);
    return () => document.removeEventListener('keydown', onKeydown, true);
  }, [enabled]);
};

export default useHidScanListener;
